import { MAP_LINE_COLOR, MAP_LINE_STYLE_SOLID, TRANSFER_STOP } from '@/lib/constants';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { RouteModel } from '@/lib/models/route/types';
import { SegmentLinkModel, SegmentTransferModel } from '@/lib/models/segment/types';
import { getAllSegmentEvents } from '@/lib/models/segment/utils/get-all-segment-events';
import { getIsSegmentTransfer } from '@/lib/models/segment/utils/get-is-segment-transfer';
import { getLinkSegments } from '@/lib/models/segment/utils/get-link-segments';
import { getSegmentStopEvents } from '@/lib/models/segment/utils/get-segment-stop-events';
import { LocationInterface, MapLocationInterface, MapRouteInterface, RouteLocationInterface } from '@/types';
import { formatRouteLocation } from '@/utils/format';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { getSegmentKeyString } from '@/utils/helpers/route-key';
import { transformSegmentLocation } from '@/utils/transform/map-route';

const generateRouteSegmentKey = (
  from?: RouteLocationInterface | LocationInterface | null,
  to?: RouteLocationInterface | LocationInterface | null,
): string => {
  if (!from || !to) return '';
  const sourcePosition = 'position' in from ? from.position : from.location;
  const targetPosition = 'position' in to ? to.position : to.location;

  // Build and return string key as per backend specifications
  return sourcePosition && targetPosition ? getSegmentKeyString({ sourcePosition, targetPosition }) : '';
};

export const getSegmentMapRoute = ({
  route,
  locationDictionary,
  showColoredLines = true,
}: {
  route: RouteModel;
  locationDictionary: LocationDictionaryModel;
  showColoredLines?: boolean;
}): MapRouteInterface => {
  const { mainOptions } = route;
  const segments = [route.start, route.startTransfer, ...mainOptions[0].segments, route.endTransfer, route.end].filter(
    (segment): segment is SegmentLinkModel | SegmentTransferModel => Boolean(segment),
  );
  const linkSegments = getLinkSegments(segments);
  const transferSegments = segments.filter((segment): segment is SegmentTransferModel => getIsSegmentTransfer(segment));

  // Build Path Pairs from Segments
  const paths = linkSegments.reduce((acc, segment) => {
    const { fromId, toId, routeType, id: segmentId, pathIds = [] } = segment;
    const from = formatRouteLocation(fromId, locationDictionary);
    const to = formatRouteLocation(toId, locationDictionary);
    const stopLocations = getAllSegmentEvents({ segments: [segment] }).map((stopEvent) => {
      const stopLocation = formatRouteLocation(stopEvent.locationId, locationDictionary);
      return transformSegmentLocation(stopLocation);
    });

    // Since a segment might have stops we need to add the stop locations between
    // the start and end of the segment, so we can present them properly on a map
    const segmentLocations: LocationInterface[] = [
      transformSegmentLocation(from),
      ...stopLocations,
      transformSegmentLocation(to),
    ].filter((location): location is LocationInterface => Boolean(location));

    // When generating a polyline we need to ensure that  the line starts and
    // ends at the port. To do this we generate an array of location pairs
    // which represents the start and end of each leg of a segment,
    // ie [[port A, Stop A], [Stop A, Port B], [Port B,]]
    const locationPairs = segmentLocations.reduce((locationAcc, location, i) => {
      if (i < segmentLocations.length - 1) {
        return [...locationAcc, [location, segmentLocations[i + 1]]];
      }
      return locationAcc;
    }, [] as LocationInterface[][]);

    const lineColor = showColoredLines ? getTransportColor(routeType, true) : MAP_LINE_COLOR;

    const mapLocation: MapLocationInterface[] = locationPairs.map((locationPair, i) => ({
      id: `${i}-${segmentId}`,
      mode: routeType,
      key: pathIds.length ? pathIds[i] : generateRouteSegmentKey(from, to),
      color: lineColor,
      style: MAP_LINE_STYLE_SOLID,
      locations: locationPair,
    }));

    return [...acc, ...mapLocation];
  }, [] as MapLocationInterface[]);

  // Build Markers Array including transfer type data
  const markers = transferSegments.map((segment) => {
    const { fromId, transferType } = segment;
    const from = formatRouteLocation(fromId, locationDictionary);
    return transformSegmentLocation(from, transferType);
  });
  const stopEvents = getSegmentStopEvents({ segments: linkSegments });
  const stopMarkers = stopEvents.map((stop) => {
    const location = formatRouteLocation(stop.locationId, locationDictionary);
    return transformSegmentLocation(location, TRANSFER_STOP);
  });

  // add on first and last marker
  const origLocation = transformSegmentLocation(formatRouteLocation(segments[0].fromId, locationDictionary));
  const destLocation = transformSegmentLocation(
    formatRouteLocation((segments[segments.length - 1] as SegmentLinkModel).toId, locationDictionary),
  );

  return {
    id: route.id,
    paths,
    markers: [origLocation, ...markers, ...stopMarkers, destLocation].filter(
      (location): location is LocationInterface => Boolean(location),
    ),
  };
};
